import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { EndeavorApi } from '../services/ApiService';
import { useData } from '../contexts/DataContext';
import './AssignmentEdit.css';
import './CourseView.css';
import './CourseEdit.css';

function AssignmentEdit() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const { clearCache, getAssignment, updateAssignment } = useData();
  
  const [assignment, setAssignment] = useState(null);
  const [endeavor, setEndeavor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    assignmentType: 'homework',
    dueDatetime: '',
    points: '',
    priority: 'medium'
  });

  // Helpers for datetime handling (preserve local datetime for datetime-local inputs)
  const toInputDateTime = useCallback((val) => {
    if (!val) return '';
    // If value contains timezone (Z or offset), convert to local components
    if (/[Zz]$/.test(val) || /[+-]\d{2}:\d{2}$/.test(val)) {
      const d = new Date(val);
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
    // If it's already a naive YYYY-MM-DDTHH:mm(:ss) string, strip seconds for input
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(val)) {
      return val.slice(0, 16);
    }
    // Fallback to Date parse
    try {
      const d = new Date(val);
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch (e) {
      return '';
    }
  }, []);

  // Fetch assignment data using DataContext (returns parsed data)
  const fetchAssignmentData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('AssignmentEdit: Fetching assignment data for ID:', assignmentId);

      const assignmentData = await getAssignment(assignmentId);
      if (!assignmentData) throw new Error('No assignment data returned');

      console.log('AssignmentEdit: Assignment data:', assignmentData);
      setAssignment(assignmentData);

      // Update form data - use toInputDateTime to avoid timezone shifts
      // Normalize priority: server may return numeric values (0-4) or strings
      const PRIORITY_NUM_TO_KEY = { 0: 'none', 1: 'low', 2: 'medium', 3: 'high', 4: 'critical' };
      let priorityValue = 'medium';
      if (typeof assignmentData.priority === 'number' && Number.isFinite(assignmentData.priority)) {
        priorityValue = PRIORITY_NUM_TO_KEY[assignmentData.priority] || 'medium';
      } else if (typeof assignmentData.priority === 'string') {
        priorityValue = assignmentData.priority.trim().toLowerCase() || 'medium';
      }

      setFormData({
        name: assignmentData.name || assignmentData.title || '',
        description: assignmentData.description || '',
        assignmentType: assignmentData.assignmentType || 'homework',
        dueDatetime: assignmentData.dueDatetime ? toInputDateTime(assignmentData.dueDatetime) : '',
        points: assignmentData.points ? assignmentData.points.toString() : '',
        priority: priorityValue
      });

      // Fetch endeavor data if assignment has endeavorId
      if (assignmentData.endeavorId) {
        try {
          const endeavorResponse = await EndeavorApi.getEndeavor(assignmentData.endeavorId);
          if (endeavorResponse.ok) {
            const endeavorData = await endeavorResponse.json();
            console.log('AssignmentEdit: Endeavor data:', endeavorData);
            setEndeavor(endeavorData);
          }
        } catch (endeavorError) {
          console.warn('AssignmentEdit: Error fetching endeavor:', endeavorError);
        }
      }

    } catch (error) {
      console.error('AssignmentEdit: Error fetching assignment data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [assignmentId, getAssignment, toInputDateTime]);

  useEffect(() => {
    if (assignmentId) {
      fetchAssignmentData();
    }
  }, [assignmentId, fetchAssignmentData]);

  // Handle form changes
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  // Handle form submission
  const handleSave = useCallback(async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      setError(null);

      // Helper to convert input (datetime-local) into a naive server datetime string without timezone
      const toServerDateTime = (val) => {
        if (!val) return null;
        const m = val.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(:\d{2})?$/);
        if (m) return `${m[1]}:00`;
        try {
          const d = new Date(val);
          const pad = (n) => String(n).padStart(2, '0');
          return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
        } catch (e) {
          return null;
        }
      };

      // Prepare the update data - include all necessary fields from original assignment
      const updateData = {
        // Include assignmentId and eventId to match server expectations (Postman examples)
        assignmentId: Number(assignmentId),
        // Server expects `title` (Postman examples) — map our UI 'name' to 'title'
        title: formData.name.trim(),
        description: formData.description.trim(),
        // Normalize assignment type to server enum (lowercase)
        assignmentType: formData.assignmentType ? String(formData.assignmentType).trim().toLowerCase() : undefined,
        dueDatetime: formData.dueDatetime ? toServerDateTime(formData.dueDatetime) : null,
        points: formData.points ? parseFloat(formData.points) : null,
        priority: formData.priority,
  endeavorId: assignment && assignment.endeavorId, // Include endeavorId from original assignment
  eventId: assignment && (assignment.eventId || assignment.eventID || assignment.event_id),
        completed: typeof assignment?.completed !== 'undefined' ? assignment.completed : false // Preserve completion status
      };

      console.log('AssignmentEdit: Updating assignment with data:', updateData);

      // Log outbound JSON
      try { console.log('AssignmentEdit: Outbound JSON:', JSON.stringify(updateData)); } catch (err) { console.warn('AssignmentEdit: stringify failed', err); }

      // Remove null/undefined fields
      const sanitized = Object.keys(updateData).reduce((acc, key) => {
        const val = updateData[key];
        if (val !== null && typeof val !== 'undefined') acc[key] = val;
        return acc;
      }, {});

      try { console.log('AssignmentEdit: Outbound (sanitized) JSON:', JSON.stringify(sanitized)); } catch (err) { console.warn('AssignmentEdit: stringify sanitized failed', err); }

      // Compute a diff of fields that changed (for logging)
      const changedFields = Object.keys(sanitized).reduce((acc, key) => {
        try {
          const before = assignment && assignment[key];
          const after = sanitized[key];
          if (String(before) !== String(after)) acc[key] = { before, after };
        } catch (e) { /* ignore */ }
        return acc;
      }, {});
      console.log('AssignmentEdit: Fields changed:', changedFields);

      const response = await updateAssignment(assignmentId, sanitized);

      if (!response.ok) {
        let errorText = '<no-response-body>';
        try { errorText = await response.text(); } catch (er) { /* ignore */ }
        console.error('AssignmentEdit: Server returned non-OK for updateAssignment', { status: response.status, body: errorText });
        throw new Error(`Failed to update assignment: ${response.status} - ${errorText}`);
      }

      // Try to parse returned JSON to inspect what changed server-side
      try {
        const responseBody = await response.json();
        console.log('AssignmentEdit: updateAssignment response body:', responseBody);
      } catch (e) {
        console.log('AssignmentEdit: updateAssignment returned no JSON body');
      }

      console.log('AssignmentEdit: Assignment updated successfully');

  // Clear cache to refresh dashboard data
  clearCache('assignment');
  clearCache('user-endeavors');
  if (assignment && assignment.endeavorId) clearCache(`endeavor_${assignment.endeavorId}`);
      // Navigate back to assignment view
      navigate(`/assignment/${assignmentId}`);

    } catch (error) {
      console.error('AssignmentEdit: Error updating assignment:', error);
      setError(error.message);
    } finally {
      setSaving(false);
    }
  }, [assignmentId, formData, assignment, navigate, clearCache, updateAssignment]);

  // Memoized date formatter
  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'Not set';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  }, []);

  // Memoized assignment icon getter
  const getAssignmentTypeIcon = useCallback((type) => {
    const typeIcons = new Map([
      ['homework', '📚'],
      ['project', '💼'],
      ['quiz', '❓'],
      ['exam', '📝'],
      ['essay', '✍️'],
      ['lab', '🧪'],
      ['reading', '📖'],
      ['discussion', '💬'],
      ['presentation', '🎤']
    ]);
    return typeIcons.get(type?.toLowerCase()) || '📋';
  }, []);

  // Form validation
  const isFormValid = useMemo(() => {
    return formData.name.trim().length > 0;
  }, [formData.name]);

  if (loading) {
    return (
      <div className="assignment-edit-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading assignment...</p>
        </div>
      </div>
    );
  }

  if (error && !assignment) {
    return (
      <div className="assignment-edit-container">
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h2>Error Loading Assignment</h2>
          <p>{error}</p>
          <div className="error-actions">
            <button 
              className="btn btn-secondary"
              onClick={() => navigate(-1)}
            >
              Go Back
            </button>
            <button 
              className="btn btn-primary"
              onClick={fetchAssignmentData}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!assignment) {
    return (
      <div className="assignment-edit-container">
        <div className="empty-state">
          <div className="empty-icon">📝</div>
          <h2>Assignment Not Found</h2>
          <p>The assignment you're trying to edit doesn't exist or has been deleted.</p>
          <button 
            className="btn btn-secondary"
            onClick={() => navigate(-1)}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="assignment-edit-container">
      {/* Back button */}
      <button 
        className="back-btn"
        onClick={() => navigate(`/assignment/${assignmentId}`)}
        aria-label="Go back to assignment view"
      >
        ← Back to Assignment
      </button>

      {/* Assignment Header */}
      <div className="assignment-header">
        <div className="assignment-title-section">
          <div className="assignment-icon-large">
            {getAssignmentTypeIcon(formData.assignmentType)}
          </div>
          <div>
            <h1 className="assignment-title">Edit Assignment</h1>
            <div className="assignment-meta">
              <span className="assignment-type">
                {formData.assignmentType.charAt(0).toUpperCase() + formData.assignmentType.slice(1)}
              </span>
              {endeavor && (
                <span className="assignment-endeavor">
                  {endeavor.name}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Assignment Edit Form */}
      <div className="assignment-content">
        <div className="assignment-section">
          <h2>
            <span className="section-icon">✏️</span>
            Assignment Details
          </h2>

          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          <form onSubmit={handleSave} className="assignment-edit-form">
            <div className="form-grid">
              {/* Assignment Name */}
              <div className="form-group span-2">
                <label htmlFor="name">Assignment Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter assignment name..."
                  required
                  className="form-input"
                />
              </div>

              {/* Assignment Type */}
              <div className="form-group">
                <label htmlFor="assignmentType">Assignment Type</label>
                <select
                  id="assignmentType"
                  name="assignmentType"
                  value={formData.assignmentType}
                  onChange={handleInputChange}
                  className="form-input"
                >
                  <option value="homework">Homework</option>
                  <option value="project">Project</option>
                  <option value="quiz">Quiz</option>
                  <option value="exam">Exam</option>
                  <option value="essay">Essay</option>
                  <option value="lab">Lab</option>
                  <option value="reading">Reading</option>
                  <option value="discussion">Discussion</option>
                  <option value="presentation">Presentation</option>
                </select>
              </div>

              {/* Priority */}
              <div className="form-group">
                <label htmlFor="priority">Priority</label>
                <select
                  id="priority"
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  className="form-input"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              {/* Due Date */}
              <div className="form-group">
                <label htmlFor="dueDatetime">Due Date & Time</label>
                <input
                  type="datetime-local"
                  id="dueDatetime"
                  name="dueDatetime"
                  value={formData.dueDatetime}
                  onChange={handleInputChange}
                  className="form-input"
                />
              </div>

              {/* Points */}
              <div className="form-group">
                <label htmlFor="points">Points</label>
                <input
                  type="number"
                  id="points"
                  name="points"
                  value={formData.points}
                  onChange={handleInputChange}
                  placeholder="e.g., 100"
                  min="0"
                  step="0.1"
                  className="form-input"
                />
              </div>

              {/* Description */}
              <div className="form-group span-2">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter assignment description, instructions, or requirements..."
                  rows={6}
                  className="form-textarea"
                />
              </div>
            </div>

            {/* Form Actions */}
            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate(`/assignment/${assignmentId}`)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!isFormValid || saving}
              >
                {saving ? (
                  <>
                    <div className="saving-spinner"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    💾 Save Changes
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Assignment Info */}
        <div className="assignment-section">
          <h2>
            <span className="section-icon">ℹ️</span>
            Assignment Information
          </h2>
          
          <div className="assignment-info-grid">
            <div className="info-row">
              <div className="info-item">
                <label>Created</label>
                <p>{formatDate(assignment.createdAt)}</p>
              </div>
              <div className="info-item">
                <label>Last Updated</label>
                <p>{formatDate(assignment.updatedAt)}</p>
              </div>
            </div>
            <div className="info-row">
              <div className="info-item">
                <label>Status</label>
                <p>
                  <span className={`status-badge ${assignment.completed ? 'completed' : 'pending'}`}>
                    {assignment.completed ? 'Completed' : 'Pending'}
                  </span>
                </p>
              </div>
              {endeavor && (
                <div className="info-item">
                  <label>Course</label>
                  <p>{endeavor.name}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="sticky-footer">
        <div className="sticky-inner">
          <div className="left-actions">
            {/* no delete action here for assignments */}
          </div>
          <div className="right-actions">
            <button className="btn btn-primary" onClick={handleSave} disabled={!isFormValid || saving}>{saving ? (<><div className="loading-spinner small"/>Saving...</>) : '💾 Save All Changes'}</button>
            <button className="btn btn-secondary" onClick={() => navigate(`/assignment/${assignmentId}`)} disabled={saving}>Cancel & Return</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AssignmentEdit;
